import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useGoogleAuth } from '@questlabs/auth-js';
import SafeIcon from '../../common/SafeIcon';
import CreateMemorialModal from '../Modals/CreateMemorialModal';
import MerchantApplicationModal from '../Modals/MerchantApplicationModal';
import NotificationCenter from '../Notifications/NotificationCenter';
import GretaAuthControl from './GretaAuthControl';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useCountryFlag } from '../../hooks/useCountryFlag';
import { useEcommerce } from '../../context/EcommerceContext';

const {
  FiFeather, FiMenu, FiX, FiUser, FiLogOut,
  FiVideo, FiSearch, FiShoppingBag, FiBook,
  FiGlobe, FiChevronDown, FiBriefcase, FiChevronRight,
  FiBell, FiShield, FiCrown, FiMusic, FiFilm, FiPackage,
} = FiIcons;

const FlagBubble = ({ flagEmoji, countryName, loading, className = '' }) => (
  <div
    aria-hidden={loading}
    aria-label={loading ? undefined : countryName}
    className={`flex items-center justify-center w-8 h-8 rounded-full bg-white/10 backdrop-blur-sm border border-accent-navy/10 shadow-sm text-base select-none cursor-default transition-opacity duration-300 ${loading ? 'opacity-40' : 'opacity-100'} ${className}`}
  >
    <span role="img" aria-label={countryName}>{flagEmoji}</span>
  </div>
);

const MobileCurrencySelect = ({ currency, supportedCurrencies, changeCurrency, flagEmoji, countryName, geoLoading }) => (
  <div className="bg-white border border-brand-dark rounded-2xl shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3.5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-brand-light flex items-center justify-center text-accent-navy flex-shrink-0">
          <SafeIcon icon={FiGlobe} className="w-4 h-4" />
        </div>
        <span className="text-base font-serif font-medium text-accent-navy">Currency</span>
      </div>
      <div className="flex items-center gap-2">
        <FlagBubble flagEmoji={flagEmoji} countryName={countryName} loading={geoLoading} className="border-accent-navy/20" />
        <select
          value={currency}
          onChange={(e) => changeCurrency(e.target.value)}
          className="appearance-none bg-brand-light border border-accent-navy/15 text-accent-navy text-xs font-black uppercase tracking-widest rounded-xl px-3 py-2 pr-7 cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent-gold/40 transition-all"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23b8860b' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
        >
          {supportedCurrencies.map((sc) => (
            <option key={sc.displayCode} value={sc.displayCode}>
              {sc.displayCode} — {sc.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

const Navbar = () => {
  const [scrolled, setScrolled]                       = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen]       = useState(false);
  const [isCreateOpen, setIsCreateOpen]               = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen]           = useState(false);
  const [isMerchantApplyOpen, setIsMerchantApplyOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen]                 = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen]           = useState(false);

  const moreMenuRef = useRef(null);

  const { user, logout, permissions, effectiveRole }          = useAuth();
  const { user: googleUser, signOut }                         = useGoogleAuth();
  const { currency, supportedCurrencies, changeCurrency }     = useCurrency();
  const { flagEmoji, countryName, loading: geoLoading }       = useCountryFlag();
  const { notifications }                                     = useEcommerce();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome   = location.pathname === '/';

  const isSignedIn   = !!user || !!googleUser;
  const unreadCount  = notifications ? notifications.filter(n => !n.is_read).length : 0;
  const isAdmin      = permissions?.isGlobalAdmin?.() || false;
  const isSuperAdmin = permissions?.isSuperAdmin?.()  || false;

  // Check if user is a merchant (own role or impersonating)
  const isMerchant = permissions?.isMerchant?.() || effectiveRole === 'merchant';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCurrencyOpen(false);
    setIsMoreMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setIsMoreMenuOpen(false);
      }
    };
    if (isMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMoreMenuOpen]);

  const handleLogout = () => {
    if (googleUser && signOut) signOut();
    logout();
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const navBg     = scrolled || !isHome || isMobileMenuOpen ? 'bg-white shadow-lg py-3' : 'bg-transparent py-6';
  const textColor = scrolled || !isHome || isMobileMenuOpen ? 'text-accent-navy' : 'text-white';

  const menuItems = [
    { label: 'Explore',     path: '/explore',     icon: FiSearch      },
    { label: 'Marketplace', path: '/marketplace', icon: FiShoppingBag },
    { label: 'Resources',   path: '/resources',   icon: FiFeather     },
    { label: 'Webinars',    path: '/webinars',    icon: FiBook        },
    { label: 'Live Events', path: '/live-events', icon: FiVideo       },
  ];

  const secondaryItems = menuItems.slice(1);

  const BellButton = ({ className = '' }) => (
    <button
      onClick={() => { setIsNotifOpen(true); setIsMobileMenuOpen(false); }}
      className={`relative p-2 transition-all hover:scale-110 ${className}`}
    >
      <SafeIcon icon={FiBell} className="w-5 h-5" />
      {unreadCount > 0 && (
        <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center border border-white leading-none">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );

  return (
    <>
      <nav className={`fixed w-full z-[100] transition-all duration-500 ease-in-out ${navBg}`}>
        <div className="container mx-auto px-4 md:px-8 flex justify-between items-center">

          {/* Logo */}
          <Link to="/"
            className={`flex items-center gap-2 font-serif text-xl md:text-2xl transition-colors relative z-[120] ${textColor}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <SafeIcon icon={FiFeather} className="text-accent-gold w-6 h-6 md:w-8 md:h-8" />
            <span className="font-bold tracking-tight">OneFinalLegacy</span>
          </Link>

          {/* Collapsed secondary nav toggle — md to xl only, sits right beside the logo */}
          <div className="hidden md:flex xl:hidden items-center relative ml-1" ref={moreMenuRef}>
            <button
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              className={`p-1.5 rounded-lg hover:bg-black/5 transition-all ${textColor}`}
            >
              <SafeIcon icon={FiMenu} className="w-5 h-5" />
            </button>
            <AnimatePresence>
              {isMoreMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  className="absolute left-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-brand-dark overflow-hidden py-2 z-50"
                >
                  {secondaryItems.map((item) => (
                    item.path === '/resources' ? (
                      <div key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setIsMoreMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 text-sm font-bold transition-colors ${
                            location.pathname === item.path
                              ? 'bg-brand-light text-accent-gold'
                              : 'text-accent-navy hover:bg-brand-light/50'
                          }`}
                        >
                          <SafeIcon icon={item.icon} className="w-4 h-4" />
                          {item.label}
                        </Link>
                        <div className="px-4 pb-2">
                          <p className="px-3 pt-1 pb-2 text-[9px] font-black uppercase tracking-widest text-text-muted">
                            Legacy &amp; Storytelling
                          </p>
                          <Link
                            to="/guided-life-interviews"
                            onClick={() => setIsMoreMenuOpen(false)}
                            className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                              location.pathname === '/guided-life-interviews'
                                ? 'bg-brand-light text-accent-gold'
                                : 'text-accent-navy hover:bg-brand-light/50'
                            }`}
                          >
                            <SafeIcon icon={FiBook} className="w-4 h-4" />
                            Life Interviews
                          </Link>
                        </div>
                      </div>
                    ) : item.path === '/live-events' ? (
                      <div key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setIsMoreMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 text-sm font-bold transition-colors ${
                            location.pathname === item.path
                              ? 'bg-brand-light text-accent-gold'
                              : 'text-accent-navy hover:bg-brand-light/50'
                          }`}
                        >
                          <SafeIcon icon={item.icon} className="w-4 h-4" />
                          {item.label}
                        </Link>
                        <div className="px-4 pb-2">
                          <p className="px-3 pt-1 pb-2 text-[9px] font-black uppercase tracking-widest text-text-muted">
                            On Demand
                          </p>
                          <Link
                            to="/replays"
                            onClick={() => setIsMoreMenuOpen(false)}
                            className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                              location.pathname === '/replays'
                                ? 'bg-brand-light text-accent-gold'
                                : 'text-accent-navy hover:bg-brand-light/50'
                            }`}
                          >
                            <SafeIcon icon={FiFilm} className="w-4 h-4" />
                            Replays
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMoreMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 text-sm font-bold transition-colors ${
                          location.pathname === item.path
                            ? 'bg-brand-light text-accent-gold'
                            : 'text-accent-navy hover:bg-brand-light/50'
                        }`}
                      >
                        <SafeIcon icon={item.icon} className="w-4 h-4" />
                        {item.label}
                      </Link>
                    )
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6 flex-1 justify-between ml-3">
            <div className="flex items-center gap-6">
              {/* Primary — always visible on desktop */}
              <NavLink to="/explore" currentPath={location.pathname} textColor={textColor}>
                Explore
              </NavLink>
              {/* Secondary — inline only when there is enough space (xl+) */}
              <div className="hidden xl:flex items-center gap-6">
                {secondaryItems.map((item) => (
                  item.path === '/resources' ? (
                    <div key={item.path} className="group relative">
                      <NavLink to={item.path} currentPath={location.pathname} textColor={textColor}>
                        {item.label}
                      </NavLink>
                      <div className="invisible absolute left-0 top-full z-50 mt-3 w-64 translate-y-1 rounded-2xl border border-brand-dark bg-white p-2 opacity-0 shadow-2xl transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
                        <p className="px-4 pt-3 pb-2 text-[9px] font-black uppercase tracking-widest text-text-muted">
                          Legacy &amp; Storytelling
                        </p>
                        <Link
                          to="/guided-life-interviews"
                          className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition-colors ${
                            location.pathname === '/guided-life-interviews'
                              ? 'bg-brand-light text-accent-gold'
                              : 'text-accent-navy hover:bg-brand-light/50'
                          }`}
                        >
                          <SafeIcon icon={FiBook} className="w-4 h-4" />
                          Life Interviews
                        </Link>
                      </div>
                    </div>
                  ) : item.path === '/live-events' ? (
                    <div key={item.path} className="group relative">
                      <NavLink to={item.path} currentPath={location.pathname} textColor={textColor}>
                        {item.label}
                      </NavLink>
                      <div className="invisible absolute left-0 top-full z-50 mt-3 w-64 translate-y-1 rounded-2xl border border-brand-dark bg-white p-2 opacity-0 shadow-2xl transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
                        <p className="px-4 pt-3 pb-2 text-[9px] font-black uppercase tracking-widest text-text-muted">
                          On Demand
                        </p>
                        <Link
                          to="/replays"
                          className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition-colors ${
                            location.pathname === '/replays'
                              ? 'bg-brand-light text-accent-gold'
                              : 'text-accent-navy hover:bg-brand-light/50'
                          }`}
                        >
                          <SafeIcon icon={FiFilm} className="w-4 h-4" />
                          Replays
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <NavLink key={item.path} to={item.path} currentPath={location.pathname} textColor={textColor}>
                      {item.label}
                    </NavLink>
                  )
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 pl-6 border-l border-accent-navy/10 relative">
              <FlagBubble flagEmoji={flagEmoji} countryName={countryName} loading={geoLoading} />

              {/* Currency Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all text-xs font-bold uppercase tracking-widest hover:bg-black/5 ${textColor}`}
                >
                  <SafeIcon icon={FiGlobe} className="w-4 h-4" />
                  <span>{currency}</span>
                  <SafeIcon icon={FiChevronDown} className={`w-3 h-3 transition-transform duration-300 ${isCurrencyOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {isCurrencyOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-brand-dark overflow-hidden py-2 z-50 max-h-80 overflow-y-auto"
                    >
                      {supportedCurrencies.map((sc) => (
                        <button key={sc.displayCode}
                          onClick={() => { changeCurrency(sc.displayCode); setIsCurrencyOpen(false); }}
                          className={`w-full px-4 py-2.5 text-left text-xs font-bold flex items-center gap-2 transition-colors ${
                            currency === sc.displayCode ? 'bg-brand-light text-accent-gold' : 'text-accent-navy hover:bg-brand-light/50'
                          }`}
                        >
                          <span className="opacity-60 w-6 shrink-0">{sc.symbol}</span>
                          <span className="truncate">{sc.name}</span>
                          <span className="ml-auto text-[10px] opacity-40 font-black shrink-0">{sc.displayCode}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Greta Auth (Google) */}
              <GretaAuthControl textColor={textColor} />

              {/* Desktop: authenticated */}
              {isSignedIn ? (
                <div className="flex items-center gap-2">
                  <BellButton className={textColor} />

                  {/* My Orders Link */}
                  <Link to="/orders"
                    className={`p-2 transition-transform hover:scale-110 ${textColor}`}
                    title="My Orders">
                    <SafeIcon icon={FiPackage} className="w-5 h-5" />
                  </Link>

                  {/* Merchant Portal Link */}
                  {isMerchant && (
                    <Link to="/merchant"
                      className={`p-2 transition-transform hover:scale-110 ${textColor}`}
                      title="Merchant Portal">
                      <SafeIcon icon={FiBriefcase} className="w-5 h-5" />
                    </Link>
                  )}

                  {/* Service Programme Link */}
                  <Link to="/programme"
                    className={`p-2 transition-transform hover:scale-110 ${textColor}`}
                    title="Service Programme">
                    <SafeIcon icon={FiMusic} className="w-5 h-5" />
                  </Link>

                  {isAdmin && (
                    <Link to="/creator-admin"
                      className={`p-2 transition-transform hover:scale-110 ${isSuperAdmin ? 'text-accent-gold' : textColor}`}
                      title={isSuperAdmin ? 'Super Administration' : 'Site Administration'}>
                      <SafeIcon icon={isSuperAdmin ? FiCrown : FiShield} className="w-5 h-5" />
                    </Link>
                  )}
                  <Link to="/user/dashboard" className={`p-2 transition-transform hover:scale-110 ${textColor}`}>
                    <SafeIcon icon={FiUser} className="w-5 h-5" />
                  </Link>
                  <button onClick={handleLogout}
                    className={`p-2 transition-transform hover:scale-110 ${textColor} hover:text-red-500`}>
                    <SafeIcon icon={FiLogOut} className="w-5 h-5" />
                  </button>
                  <button onClick={() => setIsCreateOpen(true)}
                    className="bg-accent-gold text-white px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest hover:bg-accent-navy transition-all shadow-md">
                    Create
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <button onClick={() => navigate('/login')}
                    className={`px-6 py-2.5 text-xs font-black uppercase tracking-widest border-2 rounded-full transition-all hover:scale-105 ${
                      scrolled || !isHome
                        ? 'border-accent-navy text-accent-navy hover:bg-accent-navy hover:text-white'
                        : 'border-white text-white hover:bg-white hover:text-accent-navy'
                    }`}>
                    Sign In
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden relative z-[120] flex items-center gap-1">
            <FlagBubble flagEmoji={flagEmoji} countryName={countryName} loading={geoLoading}
              className={`mr-1 ${scrolled || !isHome || isMobileMenuOpen ? 'border-accent-navy/20' : 'border-white/20 bg-white/10'}`} />
            {isSignedIn && <BellButton className={textColor} />}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 transition-transform active:scale-95 ${textColor}`}>
              <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="w-8 h-8" />
            </button>
          </div>
        </div>

        {/* Mobile Drawer */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed inset-0 bg-[#FDFCF9] z-[110] flex flex-col md:hidden pt-[72px]"
            >
              {/* Top action strip */}
              <div className="relative z-10 px-6 py-3 border-b border-accent-navy/10 bg-[#FDFCF9] flex-shrink-0">
                <GretaAuthControl textColor="text-accent-navy" className="mb-3" />
                {isSignedIn ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <Link to="/user/dashboard" onClick={() => setIsMobileMenuOpen(false)}
                      className="flex-1 flex items-center justify-center gap-2 bg-white border border-brand-dark text-accent-navy py-3 rounded-2xl font-bold text-sm shadow-sm">
                      <SafeIcon icon={FiUser} className="w-4 h-4" /> Dashboard
                    </Link>
                    <Link to="/orders" onClick={() => setIsMobileMenuOpen(false)}
                      className="flex-1 flex items-center justify-center gap-2 bg-white border border-brand-dark text-accent-navy py-3 rounded-2xl font-bold text-sm shadow-sm">
                      <SafeIcon icon={FiPackage} className="w-4 h-4" /> Orders
                    </Link>
                    {isMerchant && (
                      <Link to="/merchant" onClick={() => setIsMobileMenuOpen(false)}
                        className="flex-1 flex items-center justify-center gap-2 bg-accent-gold text-accent-navy py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-sm">
                        <SafeIcon icon={FiBriefcase} className="w-4 h-4" /> My Shop
                      </Link>
                    )}
                    {/* Service Programme Mobile Link */}
                    <Link to="/programme" onClick={() => setIsMobileMenuOpen(false)}
                      className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-accent-gold text-accent-navy py-3 rounded-2xl font-bold text-sm shadow-sm">
                      <SafeIcon icon={FiMusic} className="w-4 h-4" /> Service Programme
                    </Link>
                    {isAdmin && (
                      <Link to="/creator-admin" onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex-1 flex items-center justify-center gap-2 text-white py-3 rounded-2xl font-bold text-sm shadow-sm ${
                          isSuperAdmin ? 'bg-gradient-to-r from-purple-700 to-accent-navy' : 'bg-accent-navy'
                        }`}>
                        <SafeIcon icon={isSuperAdmin ? FiCrown : FiShield} className="w-4 h-4" />
                        {isSuperAdmin ? 'Super Admin' : 'Admin'}
                      </Link>
                    )}
                    <button onClick={() => { setIsCreateOpen(true); setIsMobileMenuOpen(false); }}
                      className="flex-1 bg-accent-gold text-accent-navy py-3 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg">
                      + Create
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }}
                      className="flex-1 bg-accent-navy text-white py-3.5 rounded-2xl font-black uppercase tracking-widest text-sm">
                      Sign In
                    </button>
                    <button onClick={() => { setIsMerchantApplyOpen(true); setIsMobileMenuOpen(false); }}
                      className="flex-1 flex items-center justify-center gap-2 bg-white border border-brand-dark text-accent-navy py-3.5 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-sm hover:border-accent-gold hover:text-accent-gold transition-all">
                      <SafeIcon icon={FiBriefcase} className="w-4 h-4" /> Sell with us
                    </button>
                  </div>
                )}
              </div>

              {/* Nav links */}
              <div className="relative z-10 flex-1 overflow-y-auto px-6 py-5 space-y-3">
                {menuItems.map((item) => (
                  item.path === '/resources' ? (
                    <React.Fragment key={item.path}>
                      <Link to={item.path}
                        className="flex items-center justify-between p-4 rounded-2xl border shadow-sm active:scale-[0.98] transition-all bg-white border-brand-dark"
                        onClick={() => setIsMobileMenuOpen(false)}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-brand-light text-accent-gold">
                            <SafeIcon icon={item.icon} />
                          </div>
                          <span className="text-xl font-serif font-medium text-accent-navy">{item.label}</span>
                        </div>
                        <SafeIcon icon={FiChevronRight} className="text-accent-gold/50" />
                      </Link>
                      <div className="ml-6 rounded-2xl border border-brand-dark bg-white px-5 py-4 shadow-sm">
                        <p className="mb-2 text-[9px] font-black uppercase tracking-widest text-text-muted">
                          Legacy &amp; Storytelling
                        </p>
                        <Link
                          to="/guided-life-interviews"
                          className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-bold transition-colors ${
                            location.pathname === '/guided-life-interviews'
                              ? 'bg-brand-light text-accent-gold'
                              : 'text-accent-navy hover:bg-brand-light/50'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <SafeIcon icon={FiBook} className="w-4 h-4" />
                          Life Interviews
                          <SafeIcon icon={FiChevronRight} className="ml-auto text-accent-gold/50" />
                        </Link>
                      </div>
                    </React.Fragment>
                  ) : item.path === '/live-events' ? (
                    <React.Fragment key={item.path}>
                      <Link to={item.path}
                        className="flex items-center justify-between p-4 rounded-2xl border shadow-sm active:scale-[0.98] transition-all bg-white border-brand-dark"
                        onClick={() => setIsMobileMenuOpen(false)}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-brand-light text-accent-gold">
                            <SafeIcon icon={item.icon} />
                          </div>
                          <span className="text-xl font-serif font-medium text-accent-navy">{item.label}</span>
                        </div>
                        <SafeIcon icon={FiChevronRight} className="text-accent-gold/50" />
                      </Link>
                      <div className="ml-6 rounded-2xl border border-brand-dark bg-white px-5 py-4 shadow-sm">
                        <p className="mb-2 text-[9px] font-black uppercase tracking-widest text-text-muted">
                          On Demand
                        </p>
                        <Link
                          to="/replays"
                          className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-bold transition-colors ${
                            location.pathname === '/replays'
                              ? 'bg-brand-light text-accent-gold'
                              : 'text-accent-navy hover:bg-brand-light/50'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <SafeIcon icon={FiFilm} className="w-4 h-4" />
                          Replays
                          <SafeIcon icon={FiChevronRight} className="ml-auto text-accent-gold/50" />
                        </Link>
                      </div>
                    </React.Fragment>
                  ) : (
                    <Link key={item.path} to={item.path}
                      className="flex items-center justify-between p-4 rounded-2xl border shadow-sm active:scale-[0.98] transition-all bg-white border-brand-dark"
                      onClick={() => setIsMobileMenuOpen(false)}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-brand-light text-accent-gold">
                          <SafeIcon icon={item.icon} />
                        </div>
                        <span className="text-xl font-serif font-medium text-accent-navy">{item.label}</span>
                      </div>
                      <SafeIcon icon={FiChevronRight} className="text-accent-gold/50" />
                    </Link>
                  )
                ))}

                <MobileCurrencySelect
                  currency={currency} supportedCurrencies={supportedCurrencies}
                  changeCurrency={changeCurrency} flagEmoji={flagEmoji}
                  countryName={countryName} geoLoading={geoLoading}
                />

                {/* Mobile logout */}
                {isSignedIn && (
                  <button onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 p-4 rounded-2xl border border-red-200 bg-red-50 text-red-600 font-bold text-sm">
                    <SafeIcon icon={FiLogOut} className="w-4 h-4" /> Sign Out
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <CreateMemorialModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
      <MerchantApplicationModal isOpen={isMerchantApplyOpen} onClose={() => setIsMerchantApplyOpen(false)} />
      <NotificationCenter isOpen={isNotifOpen} onClose={() => setIsNotifOpen(false)} />
    </>
  );
};

const NavLink = ({ to, currentPath, textColor, children }) => (
  <Link to={to}
    className={`relative font-bold transition-all text-sm tracking-wide hover:opacity-70 ${
      currentPath === to ? 'text-accent-gold' : textColor
    }`}>
    {children}
    {currentPath === to && (
      <motion.div layoutId="nav-underline"
        className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-accent-gold rounded-full" />
    )}
  </Link>
);

export default Navbar;